import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, Subject, catchError, debounceTime, map, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { ChatRoomDTO } from '../models/chat-room-dto';
import { ChatUserSelectDTO } from '../models/chat-user-select-dto';
import { CreateChatRoomDTO } from '../models/create-chat-room-dto';
import { ChatUsersDTO } from '../models/chat-users-dto';
import { ChatMessageDTO } from '../models/chat-message-dto';
import { EditChatMessageDTO } from '../models/edit-chat-message-dto';
import { RenameChatRoomDTO } from '../models/rename-chat-room-dto';

/**
 * Motivo de un error de negocio del chat: ProblemDetails.detail de un 400/403/404 (backend,
 * BusinessExceptionFilterAttribute sobre ChatController). undefined para cualquier otro error
 * (500, red, validación de modelo...) — Helpers.showError cae entonces en su texto genérico.
 */
export function getChatErrorDetail(error: unknown): string | undefined {
  if (!(error instanceof HttpErrorResponse) || ![400, 403, 404].includes(error.status)) return undefined;

  const detail = error.error?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : undefined;
}

// Espera tras el último aviso de cambio antes de pedir las salas: agrupa en una sola petición la
// ráfaga de avisos que dispara un mismo mensaje (evento SignalR + markAsRead + ...).
const ROOMS_REFRESH_DEBOUNCE_MS = 300;

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly url = `${environment.urlAPI}Chat`;
  private readonly userService = inject(UserService);

  /**
   * Estado ÚNICO de las salas de la instalación seleccionada, compartido por el badge del navbar
   * (DefaultLayoutComponent), la lista de salas y la sala abierta (ChatModalComponent). Antes cada uno
   * pedía GetRooms por su cuenta a cada aviso — ~6 GetRooms por mensaje recibido (CHAT_REVISION.md B8).
   */
  private readonly roomsState = signal<ChatRoomDTO[]>([]);
  readonly rooms = this.roomsState.asReadonly();

  // false hasta la primera carga correcta (al arrancar, tras cambiar de instalación o tras cerrar
  // sesión): distingue "no tienes salas" de "todavía no se han cargado".
  private readonly roomsLoadedState = signal(false);
  readonly roomsLoaded = this.roomsLoadedState.asReadonly();

  // La última recarga falló. Se conservan las últimas salas conocidas: un fallo de un refresco en
  // segundo plano no debe vaciar la lista ni sacar un aviso por cada evento de SignalR.
  private readonly roomsLoadErrorState = signal(false);
  readonly roomsLoadError = this.roomsLoadErrorState.asReadonly();

  private readonly refreshRequests = new Subject<void>();

  // Se incrementa con cada aviso y con cada vaciado: una respuesta de GetRooms solo se aplica si no ha
  // habido ninguno desde que se lanzó. Si no, sus datos pueden ser anteriores al cambio que se acaba
  // de avisar (p. ej. una sala recién creada que la modal ya tiene abierta, y que esa respuesta vieja
  // no incluye) o de otra instalación/usuario.
  private refreshSeq = 0;

  constructor(private http: HttpClient) {
    this.refreshRequests.pipe(
      debounceTime(ROOMS_REFRESH_DEBOUNCE_MS),
      switchMap(() => {
        const seq = this.refreshSeq;
        return this.http.get<ChatRoomDTO[]>(`${this.url}/GetRooms`).pipe(
          map(rooms => ({ seq, rooms, failed: false })),
          catchError(() => of({ seq, rooms: [] as ChatRoomDTO[], failed: true }))
        );
      })
    ).subscribe(({ seq, rooms, failed }) => {
      if (seq !== this.refreshSeq) return;

      this.roomsLoadErrorState.set(failed);
      if (failed) return;

      this.roomsState.set(rooms);
      this.roomsLoadedState.set(true);
    });

    // Cambio de instalación en el navbar: todo el estado de salas es de la instalación anterior — se
    // vacía y se vuelve a pedir. DefaultLayoutComponent solo cambia este signal DESPUÉS de que
    // UpdateLastInstallation haya terminado, así que GetRooms ya lee la instalación nueva en el backend.
    effect(() => {
      const idInstallation = this.userService.selectedInstallationId();
      untracked(() => {
        this.clearRooms();
        if (idInstallation) this.notifyRoomsChanged();
      });
    });
  }

  /**
   * Pide una recarga de las salas (con debounce: varios avisos seguidos = una sola petición). La llama
   * cualquier cosa que cambie salas, previews o no-leídos: mensaje recibido por SignalR, envío, lectura,
   * crear sala, miembros, salir... Todos los consumidores leen el resultado de `rooms`.
   */
  notifyRoomsChanged(): void {
    this.refreshSeq++;
    this.refreshRequests.next();
  }

  /**
   * Vacía el estado de salas y descarta cualquier recarga en vuelo. Además de en el cambio de
   * instalación, al cerrar sesión (DefaultLayoutComponent.ngOnDestroy): este servicio es root y
   * sobrevive al logout en modo JWT (sin recargar la página) — sin esto, el siguiente usuario vería
   * durante un instante las salas del anterior.
   */
  clearRooms(): void {
    this.refreshSeq++;
    this.roomsState.set([]);
    this.roomsLoadedState.set(false);
    this.roomsLoadErrorState.set(false);
  }

  canCreateRoom(): Observable<boolean> {
    return this.http.get<boolean>(`${this.url}/CanCreateRoom`);
  }

  getSelectableUsers(): Observable<ChatUserSelectDTO[]> {
    return this.http.get<ChatUserSelectDTO[]>(`${this.url}/GetSelectableUsers`);
  }

  getRoomMembers(idChat: string): Observable<ChatUserSelectDTO[]> {
    return this.http.get<ChatUserSelectDTO[]>(`${this.url}/GetRoomMembers/${idChat}`);
  }

  createRoom(dto: CreateChatRoomDTO): Observable<ChatRoomDTO> {
    return this.http.post<ChatRoomDTO>(`${this.url}/CreateRoom`, dto);
  }

  addUsers(idChat: string, userIds: string[], includeHistory: boolean): Observable<void> {
    const dto: ChatUsersDTO = { UserIds: userIds, IncludeHistory: includeHistory };
    return this.http.post<void>(`${this.url}/AddUsers/${idChat}`, dto);
  }

  removeUsers(idChat: string, userIds: string[]): Observable<void> {
    const dto: ChatUsersDTO = { UserIds: userIds };
    return this.http.post<void>(`${this.url}/RemoveUsers/${idChat}`, dto);
  }

  renameRoom(idChat: string, title: string): Observable<ChatRoomDTO> {
    const dto: RenameChatRoomDTO = { Title: title };
    return this.http.put<ChatRoomDTO>(`${this.url}/RenameRoom/${idChat}`, dto);
  }

  leaveRoom(idChat: string): Observable<void> {
    return this.http.post<void>(`${this.url}/LeaveRoom/${idChat}`, {});
  }

  getMessages(idChat: string, beforeMessageId?: string, take = 10): Observable<ChatMessageDTO[]> {
    let params = new HttpParams().set('take', take);
    if (beforeMessageId) params = params.set('beforeMessageId', beforeMessageId);
    return this.http.get<ChatMessageDTO[]>(`${this.url}/GetMessages/${idChat}`, { params });
  }

  // Multipart (no JSON) para poder mandar el archivo adjunto opcional — el backend espera [FromForm].
  sendMessage(idChat: string, message: string, file?: File): Observable<ChatMessageDTO> {
    const formData = new FormData();
    formData.append('Message', message);
    if (file) formData.append('FileAttached', file);
    return this.http.post<ChatMessageDTO>(`${this.url}/SendMessage/${idChat}`, formData);
  }

  // Mensaje por voz: el backend transcribe el WAV con Whisper y lo envía como un mensaje de texto
  // normal — misma respuesta que sendMessage.
  sendVoiceMessage(idChat: string, audio: Blob): Observable<ChatMessageDTO> {
    const formData = new FormData();
    formData.append('audio', audio, 'audio.wav');
    return this.http.post<ChatMessageDTO>(`${this.url}/SendVoiceMessage/${idChat}`, formData);
  }

  markAsRead(idChat: string): Observable<void> {
    return this.http.post<void>(`${this.url}/MarkAsRead/${idChat}`, {});
  }

  editMessage(idMessage: string, message: string): Observable<ChatMessageDTO> {
    const dto: EditChatMessageDTO = { Message: message };
    return this.http.put<ChatMessageDTO>(`${this.url}/EditMessage/${idMessage}`, dto);
  }

  deleteMessage(idMessage: string): Observable<ChatMessageDTO> {
    return this.http.post<ChatMessageDTO>(`${this.url}/DeleteMessage/${idMessage}`, {});
  }
}
